import { actionRenewSyncToken } from "@/app/(actions)/renewSyncToken.action"
import { useSyncStore } from "@/app/(store)/sync.store"
import { SyncClient } from "twilio-sync"
import pako from "pako"
import { Buffer } from "buffer"
import {
  AccountSchema,
  ClusterSchema,
  TLoginUserData,
  UserPreferencesSchema,
  UserStatusSchema,
} from "@/schemas/user.schema"

//////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////
/**
 * About this file:
 *
 * Currently this only supports documents and not maps (although we can support them).
 * Currently it only supports the following document types:
 * - account
 * - userStatus
 * - userPreferences
 * - cluster
 * The rest of the document types should be linked to the corresponding schema and the corresponding state.
 * Also they would need to be enabled in the SYNC_CONNECTIONS object.
 * To avoid reinitializing the sync connection, about switching pages using window.location methods, only
 * use the router methods from Next.js (next/navigation package).
 *
 * To finish the sync implementation, check how I did it in the mobile app at https://github.com/ringio/ringio-mobile-app/blob/update/src/services/SyncService.ts
 *
 * The sync states are preinitialized with the user data from the login method obtained in the first server call, so if it always safe to cast the states to their corresponding schemas, they will always be valid.
 */
//////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////

type SyncDocumentType =
  | "account"
  | "departmentStats"
  | "dialerList"
  | "intention"
  | "lastRing"
  | "liveCalls"
  | "paymentStatus"
  | "recentCalls"
  | "roster"
  | "smartView"
  | "userPreferences"
  | "userStats"
  | "userStatus"
  | "cluster"
type SyncMapType = "contacts" | "smsHistories"

const SYNC_CONNECTIONS: Record<SyncDocumentType, boolean> = {
  account: true,
  departmentStats: false,
  dialerList: false,
  intention: false,
  lastRing: false,
  liveCalls: false,
  paymentStatus: false,
  recentCalls: false,
  roster: false,
  smartView: false,
  userPreferences: true,
  userStats: false,
  userStatus: true,
  cluster: true,
}

/**
 * Inflates a base64-encoded data object.
 * If the data object is not deflated, it returns the original data object.
 * If the data object is deflated, it inflates it and returns the inflated object.
 * If an error occurs during the inflation process, it logs the error and returns an empty object.
 *
 * @param data - The base64-encoded data object to inflate.
 * @returns The inflated data object or an empty object if an error occurs.
 */
function inflateBase64(data: any): Record<string, unknown> {
  if (!data?.deflated) {
    return data || {}
  }

  try {
    let t1 = data.deflated
    let t2 = Buffer.from(t1, "base64")
    let t3 = pako.inflate(t2, { to: "string" })
    let t4 = JSON.parse(t3)

    return (data && data.deflated ? t4 : data) || {}
  } catch (err) {
    console.error(`Error inflating data: ${err}`, JSON.stringify(data))
    return {}
  }
}

/**
 * Inflates a map item.
 * @param item - The item to be inflated.
 * @returns The inflated item.
 */
function inflateMapItem(item: any) {
  return inflateBase64(item)
}

interface ILoadDocumentProps {
  documentName: string
  type: SyncDocumentType
}

/**
 * Loads a document from the sync client and sets up event listeners for updates, failures, and removals.
 * @param documentName - The name of the document to load.
 * @param type - The type of the document.
 */
async function loadDocument({ documentName, type }: ILoadDocumentProps) {
  if (
    !useSyncStore.getState()._.syncClient ||
    useSyncStore.getState()._.syncClient?.connectionState !== "connected"
  ) {
    console.warn("Sync client is not ready!")
    return
  }

  const syncClient = useSyncStore.getState()._.syncClient!

  // Check if we want to load it
  // if (!SYNC_CONNECTIONS[type]) {
  //   console.warn(`We don't want to load ${documentName}!`)
  //   return
  // }

  // Check if the doc is already in syncConnections and if it is
  // there, unregister it and its listeners first
  if (useSyncStore.getState()._.initialization.documents[type]) {
    // TODO: Maybe we want to restart the connection?
    // unloadSyncConnection(documentName)
    console.warn(`${documentName} is already loaded!`)
    return
  }

  // const syncConnectionType: SyncConnectionType = "document"
  // const syncConnectionSubType: SyncConnectionSubType =
  //   getConnectionSubType(documentName)

  const syncDoc = await syncClient.document(documentName)
  console.debug(`Connected sync to ${documentName}`)

  // syncDoc.on("_subscriptionStateChanged", (state: string) => {
  //   console.debug(`Document ${documentName} subscription state changed`, state)
  // })

  // syncDoc.on("contextUpdated", (e: any) => {
  //   console.debug(`Received Document context update event from ${documentName}`)
  // })

  // syncDoc.on("messagePublished", (e: any) => {
  //   console.debug(
  //     `Received Document message published event from ${documentName}`
  //   )
  // })

  // syncDoc.on("itemAdded", (e: any) => {
  //   console.debug(`Received Document item added event from ${documentName}`)
  // })

  // syncDoc.on("itemUpdated", (e: any) => {
  //   console.debug(`Received Document item updated event from ${documentName}`)
  // })

  syncDoc.on("updated", (e: any) => {
    console.warn(e)
    const _item: Record<string, unknown> = e?.data?.deflated
      ? inflateMapItem(e?.data)
      : e?.data

    console.debug(`Received Document update event from ${documentName}`)

    switch (type) {
      case "userStatus": {
        const parsedItem = UserStatusSchema.safeParse(_item)
        if (!parsedItem.success) {
          console.error(
            `We will ignore the update from ${documentName} because the data is invalid`,
            parsedItem.error
          )
          break
        }

        useSyncStore.setState({
          userStatus: parsedItem.data,
        })
        console.debug(`Updated state from ${documentName}`)
        break
      }
      case "account": {
        const parsedItem = AccountSchema.safeParse(_item)
        if (!parsedItem.success) {
          console.error(
            `We will ignore the update from ${documentName} because the data is invalid`,
            parsedItem.error
          )
          break
        }

        useSyncStore.setState({
          account: parsedItem.data,
        })
        console.debug(`Updated state from ${documentName}`)
        break
      }
      case "cluster": {
        const parsedItem = ClusterSchema.safeParse(_item)
        if (!parsedItem.success) {
          console.error(
            `We will ignore the update from ${documentName} because the data is invalid`,
            parsedItem.error
          )
          break
        }

        useSyncStore.setState({
          cluster: parsedItem.data,
        })
        console.debug(`Updated state from ${documentName}`)
        break
      }
      case "userPreferences": {
        const parsedItem = UserPreferencesSchema.safeParse(_item)
        if (!parsedItem.success) {
          console.error(
            `We will ignore the update from ${documentName} because the data is invalid`,
            parsedItem.error
          )
          break
        }

        useSyncStore.setState({
          userPreferences: parsedItem.data,
        })
        console.debug(`Updated state from ${documentName}`)
        break
      }
      default:
        break
    }

    // switch (syncConnectionSubType) {
    //   case "account":
    //     updateDoc({
    //       docType: "account",
    //       docValue: _item,
    //     })
    //     break
    //   case "lastRing":
    //     if (Object.entries(_item).length < 1) return
    //     updateDoc({
    //       docType: "lastRing",
    //       docValue: _item,
    //     })
    //     break
    //   case "userStatus":
    //     updateDoc({
    //       docType: "userStatus",
    //       docValue: _item,
    //     })
    //     break
    //   case "userPreferences":
    //     updateDoc({
    //       docType: "userPreferences",
    //       docValue: _item,
    //     })
    //     break
    //   default:
    //     break
    // }
  })

  syncDoc.on("failure", () => {
    console.error(`Document ${documentName} failed`)
    // TODO: Maybe we want to restart the connection?
  })

  syncDoc.on("removed", () => {
    console.debug(`Document ${documentName} removed`)
    // TODO: Maybe we want to restart the connection?
    // unloadSyncConnection(documentName)

    useSyncStore.setState((oldState) => ({
      _: {
        ...oldState._,
        initialization: {
          ...oldState._.initialization,
          documents: {
            ...oldState._.initialization.documents,
            [type]: false,
          },
        },
      },
    }))
  })

  useSyncStore.setState((oldState) => ({
    _: {
      ...oldState._,
      initialization: {
        ...oldState._.initialization,
        documents: {
          ...oldState._.initialization.documents,
          [type]: true,
        },
      },
    },
  }))
}

/**
 * Handles the renewal of the sync token for the given SyncClient.
 * @param syncClient - The SyncClient instance.
 * @returns A Promise that resolves when the sync token is successfully renewed.
 */
async function handlesyncTokenRenewal(syncClient: SyncClient) {
  try {
    const newSyncToken = await actionRenewSyncToken()
    syncClient.updateToken(newSyncToken)
  } catch (err) {
    console.error("Error updating the token", err)
    useSyncStore.setState({ status: "error" })
    // TODO: Maybe retry the token update?
  }
}

interface IInitializeTwilioSyncClientProps {
  token: string
  initialUserData: TLoginUserData
}

export function initializeTwilioSyncClient({
  token,
  initialUserData,
}: IInitializeTwilioSyncClientProps) {
  console.warn("Initializing sync client")
  if (
    (useSyncStore.getState().status !== "error" &&
      useSyncStore.getState().status !== "disconnected" &&
      useSyncStore.getState().status !== "unknown") ||
    useSyncStore.getState()._.syncClient
  ) {
    console.warn("Sync client already initialized")
    return
  }

  if (!token) {
    console.error("Token is empty")
    return
  }

  const items: Record<string, string> = initialUserData.twilio.ids

  const syncClient = new SyncClient(token, {
    logLevel: process.env.NEXT_PUBLIC_ENV === "production" ? "error" : "warn",
  })

  syncClient.on("connectionError", (error) => {
    console.error("Connection error", error)
    useSyncStore.setState({ status: "error" })
  })

  syncClient.on("connectionStateChanged", (state) => {
    console.log("Connection state changed", state)
    useSyncStore.setState({ status: state })

    if (state === "connected") {
      for (const [key, value] of Object.entries(items)) {
        switch (key) {
          case "account":
            // No need to await
            loadDocument({
              documentName: value,
              type: "account",
            })
            break
          case "departmentStats":
            // No need to await
            loadDocument({
              documentName: value,
              type: "departmentStats",
            })
            break
          case "dialerList":
            // No need to await
            loadDocument({
              documentName: value,
              type: "dialerList",
            })
            break
          case "intention":
            // No need to await
            loadDocument({
              documentName: value,
              type: "intention",
            })
            break
          case "lastRing":
            // No need to await
            loadDocument({
              documentName: value,
              type: "lastRing",
            })
            break
          case "liveCalls":
            // No need to await
            loadDocument({
              documentName: value,
              type: "liveCalls",
            })
            break
          case "paymentStatus":
            // No need to await
            loadDocument({
              documentName: value,
              type: "paymentStatus",
            })
            break
          case "recentCalls":
            // No need to await
            loadDocument({
              documentName: value,
              type: "recentCalls",
            })
            break
          case "roster":
            // No need to await
            loadDocument({
              documentName: value,
              type: "roster",
            })
            break
          case "smartView":
            // No need to await
            loadDocument({
              documentName: value,
              type: "smartView",
            })
            break
          case "userPreferences":
            // No need to await
            loadDocument({
              documentName: value,
              type: "userPreferences",
            })
            break
          case "userStats":
            // No need to await
            loadDocument({
              documentName: value,
              type: "userStats",
            })
            break
          case "userStatus":
            // No need to await
            loadDocument({
              documentName: value,
              type: "userStatus",
            })
            break
          case "cluster":
            // No need to await
            loadDocument({
              documentName: value,
              type: "cluster",
            })
            break
          default:
            break
        }
      }
    }
  })

  syncClient.on("tokenAboutToExpire", async () => {
    console.log("Token about to expire")
    await handlesyncTokenRenewal(syncClient)
  })

  syncClient.on("tokenExpired", async () => {
    console.error("Token expired")
    await handlesyncTokenRenewal(syncClient)
  })

  useSyncStore.setState((oldState) => ({
    _: {
      ...oldState._,
      syncClient,
    },
    // This is type-safe since initialUserData schema is validated
    userStatus: initialUserData.user,
    account: initialUserData.account,
    cluster: initialUserData.cluster,
    userPreferences: initialUserData.preferences,
  }))
}
